import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { getPlans } from 'actions';
import FeaturePageShell from 'components/shell/FeaturePageShell';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import styles from './Billing.module.scss';

const formatLimit = value => (
  typeof value === 'number' ? new Intl.NumberFormat().format(value) : '—'
);

export const PlanCard = ({ plan, localizeMessage }) => {
  const title = plan.name || plan.planName || plan.title || plan.id;
  const isFree = plan.isFree || plan.price === 0;
  const price = isFree
    ? localizeMessage('billing.plan.free')
    : plan.price == null
      ? '—'
      : `${plan.price} ${plan.currency || 'USD'}`;

  return (
    <article className={styles.planCard} data-testid="billing-plan">
      <div className={styles.planHeader}>
        <div>
          <h2 className={styles.planTitle}>{title}</h2>
          <p className={styles.planPrice}>
            {price}
            {!isFree && plan.price != null && (
              <span>{localizeMessage('billing.plan.perMonth')}</span>
            )}
          </p>
        </div>
        {plan.isActive && (
          <span className={styles.currentBadge}>{localizeMessage('billing.plan.current')}</span>
        )}
      </div>

      <dl className={styles.limits}>
        <div>
          <dt>{localizeMessage('billing.plan.searchSessions')}</dt>
          <dd>{formatLimit(plan.maxQueries)}</dd>
        </div>
        <div>
          <dt>{localizeMessage('billing.plan.products')}</dt>
          <dd>{formatLimit(plan.maxProducts)}</dd>
        </div>
      </dl>

      {!plan.isActive && plan.warning && (
        <p className={styles.warning}>{plan.warning}</p>
      )}
    </article>
  );
};

PlanCard.propTypes = {
  plan: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    planName: PropTypes.string,
    title: PropTypes.string,
    price: PropTypes.number,
    currency: PropTypes.string,
    isFree: PropTypes.bool,
    isActive: PropTypes.bool,
    maxQueries: PropTypes.number,
    maxProducts: PropTypes.number,
    warning: PropTypes.string,
  }).isRequired,
  localizeMessage: PropTypes.func.isRequired,
};

const Billing = () => {
  const dispatch = useDispatch();
  const localizeMessage = useLocalizedMessage();
  const {
    app: { currentShop },
    billing: { plans = {} },
  } = useSelector(store => store);
  const { isFetching, data = [], error } = plans;

  const loadPlans = useCallback(() => {
    dispatch(getPlans(currentShop, 'search'));
  }, [currentShop, dispatch]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  return (
    <FeaturePageShell
      title={localizeMessage('sidebar.billing.plans')}
      description={localizeMessage('billing.description')}
      width="wide"
      isLoading={isFetching}
      error={error ? localizeMessage('billing.error') : null}
      onRetry={error ? loadPlans : null}
    >
      {data.length > 0 ? (
        <div className={styles.planGrid}>
          {data.map(plan => (
            <PlanCard
              key={plan.id || plan.title}
              plan={plan}
              localizeMessage={localizeMessage}
            />
          ))}
        </div>
      ) : (
        <section className={styles.emptyState} data-testid="billing-empty-state">
          <span className={styles.emptyIcon} aria-hidden="true">
            <i className="zmdi zmdi-card" />
          </span>
          <div>
            <h2>{localizeMessage('billing.empty.title')}</h2>
            <p>{localizeMessage('billing.empty.description')}</p>
            <a href="mailto:support@kealabs.com">support@kealabs.com</a>
          </div>
        </section>
      )}
    </FeaturePageShell>
  );
};

export default Billing;
